package components.accounting

import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.invoice.InvoiceNumber
import it.neckar.open.collections.fastForEach
import it.neckar.open.kotlin.lang.SpecialChars
import it.neckar.react.common.*
import it.neckar.react.common.table.*
import plannerI18nConfiguration
import react.*
import react.dom.*
import services.http.CompanyRevenue

/**
 * Collects the projects for each year.
 * Visualizes the sum of the earnings for each year.
 *
 */
val InvoiceTable: FC<RevenueTableProps> = fc("InvoiceTable") { props ->
  val lizergyRevenue = props::lizergyRevenue.safeGet()
  val neckarITRevenue = props::neckarITRevenue.safeGet()
  val partnerRevenues = props::partnerRevenues.safeGet()

  val allRevenues = buildList {
    lizergyRevenue?.let { add(it) }
    neckarITRevenue?.let { add(it) }
    partnerRevenues?.let { addAll(it) }
  }

  val sortedByFunction = useState(
    SortedByFunction(
      sortedBy = SortedBy.SortedDescending,
      tableDataColumn = totalRevenueColumn,
    )
  )

  val invoiceNumberColumns = buildList {
    allRevenues.flatMap { it.revenueByInvoiceNumber.keys }.distinct().sortedBy { it?.value }.fastForEach { invoiceNumber ->
      add(getInvoiceNumberRevenueColumn(invoiceNumber))
    }
  }

  val columns = buildList {
    add(companyColumn)
    invoiceNumberColumns.fastForEach { add(it) }
    add(totalRevenueColumn)
  }


  div("my-3") {

    busyIfNull(lizergyRevenue) { loadedLizergyRevenue ->
      table(
        columns = columns,
        entries = allRevenues,
        footerEntry = CompanyRevenue("Gesamt", allRevenues.flatMap { it.revenues }),
        sortedByFunction = sortedByFunction,
      ) {}
    }

  }

}


val companyColumn: TableHeaderColumn<CompanyRevenue>
  get() = TableHeaderColumn(
    id = "company",
    title = "Firma",
    sortFunction = compareBy { it.companyName },
  ) { companyRevenue ->
    {
      +companyRevenue.companyName
    }
  }

fun getInvoiceNumberRevenueColumn(invoiceNumber: InvoiceNumber?): TableDataColumn<CompanyRevenue> {
  return TableDataColumn(
    id = "${invoiceNumber?.value}",
    title = "${invoiceNumber?.value}",
    titleClasses = "justify-content-end",
    sortFunction = compareBy { it.revenueByInvoiceNumber[invoiceNumber] },
  ) { companyRevenue ->
    {
      attrs {
        addClass("text-end")
      }

      +buildString {
        companyRevenue.revenueByInvoiceNumber[invoiceNumber]?.let { revenue ->
          appendLine(revenue.format(plannerI18nConfiguration))
        }
        if (companyRevenue.companyName == "Neckar IT") {
          companyRevenue.projectCountByInvoiceNumber[invoiceNumber]?.let { count ->
            appendLine("($count${SpecialChars.Nbsp}Projekte)")
          }
        }
      }
    }
  }
}

val totalRevenueColumn: TableDataColumn<CompanyRevenue>
  get() = TableDataColumn(
    id = "totalRevenue",
    title = "Gesamt",
    titleClasses = "justify-content-end",
    sortFunction = compareBy { it.totalRevenue },
  ) { companyRevenue ->
    {
      attrs {
        addClass("text-end")
      }

      +companyRevenue.totalRevenue.format(plannerI18nConfiguration)
    }
  }


external interface RevenueTableProps : Props {
  var lizergyRevenue: CompanyRevenue?
  var neckarITRevenue: CompanyRevenue?
  var partnerRevenues: List<CompanyRevenue>?
}

